import { T } from "../tokens";
import { useAudioScrubber } from "../hooks/useAudioScrubber";
import { forwardRef, useImperativeHandle } from "react";
import { MonoData } from "./MonoData";
import { MonoLabel } from "./MonoLabel";
import { SoundBars } from "./SoundBars";
import { TextBtn } from "./TextBtn";

export interface MiniPlayerProps {
  /** A ready-to-play URL: a blob: preview URL or a Storage download URL.
   *  Empty string = nothing to play yet (idle / still resolving). */
  src: string;
  /** Fallback seconds (echo.duration) shown until <audio> metadata loads. */
  fallbackDuration: number;
  /** True while the caller is still resolving `src` (e.g. fetching the
   *  download URL) — shows「· 載入中」instead of the transport. */
  loading?: boolean;
  /** Start playback as soon as the media element is mounted. Intended for a
   *  player opened by an explicit user click. */
  autoPlay?: boolean;
}

export interface MiniPlayerHandle {
  play: () => Promise<void>;
  pause: () => void;
}

/** mm:ss — local to @goodbye/ui so this stays self-contained (the apps have
 *  their own formatDuration in lib, but the UI package shouldn't reach into
 *  an app). */
function mmss(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Compact grayscale audio player: hidden native <audio> + SoundBars + a 1px
 * hairline scrub bar (role=slider) + play/pause + time. No <audio controls>,
 * no <progress>, no colour, square corners — DESIGN.md compliant. Shared by
 * the learner Echo preview/replay and the worker echo replay.
 */
export const MiniPlayer = forwardRef<MiniPlayerHandle, MiniPlayerProps>(function MiniPlayer({
  src,
  fallbackDuration,
  loading = false,
  autoPlay = false,
}, ref) {
  const player = useAudioScrubber(src, fallbackDuration);
  useImperativeHandle(ref, () => ({ play: player.play, pause: player.pause }), [
    player.pause,
    player.play,
  ]);

  if (loading || !src) {
    return (
      <div
        style={{
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MonoLabel size={11} color={T.ink350}>
          · 載 入 中
        </MonoLabel>
      </div>
    );
  }

  return (
    <div>
      <audio
        ref={player.audioRef}
        src={src}
        autoPlay={autoPlay}
        {...player.audioProps}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", justifyContent: "center", paddingBottom: 6 }}>
        <SoundBars playing={player.playing} />
      </div>
      <div
        role="slider"
        tabIndex={0}
        aria-label="播放進度"
        aria-valuemin={0}
        aria-valuemax={Math.round(player.total)}
        aria-valuenow={Math.round(player.elapsed)}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          const r = e.currentTarget.getBoundingClientRect();
          if (r.width > 0) player.seekToFraction((e.clientX - r.left) / r.width);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 0) return;
          const r = e.currentTarget.getBoundingClientRect();
          if (r.width > 0) player.seekToFraction((e.clientX - r.left) / r.width);
        }}
        style={{
          position: "relative",
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          touchAction: "none",
          outline: "none",
        }}
      >
        <div style={{ position: "relative", width: "100%", height: 2, background: T.ink200 }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${player.total > 0 ? (player.elapsed / player.total) * 100 : 0}%`,
              background: T.ink900,
            }}
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <TextBtn onClick={() => void player.togglePlay()} mono>
          {player.playing ? "暫 停" : "播 放"}
        </TextBtn>
        <MonoData size={11}>
          {mmss(player.elapsed)} / {mmss(player.total)}
        </MonoData>
      </div>
    </div>
  );
});
